import { Collapse, Modal } from 'antd';
// import mockTestResult from './mockTestResult';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { useTranslation } from 'react-i18next';
import { Dropdown, Empty, Table } from '@vindr/ui';
import './TestResult.css';
import cx from '@vindr/utils/helpers/cx';
import { CaretDownOutlined } from '@vindr/icons';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useEffect, useState } from 'react';
import { Order } from '@vindr/services/apis/order.api';
import { useGetTestInformation } from '@vindr/services/actions/report.action';
import { TestInformation } from '@vindr/services/apis/report.api';
import dayjs from 'dayjs';

const { Panel } = Collapse;

interface TestResultProps {
  order: Order;
}

interface TestResult {
  level: number;
  name: string;
  result: string;
  normalRange: string;
  higherLimit: string;
  lowerLimit: string;
  unit: string;
  updateTime: string;
}

const TestResult = ({ order, onClose, visible }: TestResultProps) => {
  const { t } = useTranslation('Vindoc');
  const [selectedInstitution, setSelectedInstitution] = useState(order?.institution);

  const { data: Institution } = useGetInstitutions({ limit: 100, offset: 0 });
  const institutionMenuItems = Institution?.data?.map((item) => ({
    key: item.id,
    label: item.name,
    onClick: () => setSelectedInstitution(item),
  }));

  const { data: testResult, isFetching } = useGetTestInformation(
    { patient_id: order?.visited_patient?.patient_id, institution_id: selectedInstitution?.id },
    // { patient_id: '200531031', institution_id: 'c8a8554f-b065-4710-926f-41d6758cd86b'},
    { enabled: !!order?.visited_patient?.patient_id && !!selectedInstitution?.id }
  );
  const resultByDate = (testResult?.data || []).map((item) => {
    return {
      date: item.date_id,
      data: transformTestResult(item.categories),
    };
  });

  useEffect(() => {
    if (!selectedInstitution) {
      setSelectedInstitution(Institution?.data[0]);
    }
  }, [Institution]);

  const definedColumns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (text: string, record: TestResult) => {
        if (record.level === 1) {
          return <div className="font-bold uppercase">{text}</div>;
        }
        if (record.level === 2) {
          return <div className="ml-2 font-bold">{text}</div>;
        }
        return <div className="ml-4">{text}</div>;
      },
    },
    {
      title: t('Result'),
      dataIndex: 'result',
      key: 'result',
      width: 120,
      className: 'relative',
      render: (text: string, record: TestResult) => {
        let warning = false;
        if (
          text &&
          (text.toLowerCase().includes('positive') ||
            text.toLowerCase().includes('dương tính') ||
            text.toLowerCase().includes('bất thường'))
        ) {
          warning = true;
        } else {
          const lowerLimit = parseFloat(record.lowerLimit);
          const higherLimit = parseFloat(record.higherLimit);
          const result = parseFloat(text);
          if (
            (lowerLimit && result && result < lowerLimit) ||
            (higherLimit && result && result > higherLimit)
          ) {
            warning = true;
          }
        }
        return (
          <>
            <div
              className={`${
                warning ? 'bg-red-900' : ''
              } absolute top-0 left-0 flex h-full w-full items-center p-2`}
            ></div>
            <div className="relative">
              <span>{text}</span>
            </div>
          </>
        );
      },
    },
    {
      title: t('Normal Range'),
      dataIndex: 'normalRange',
      key: 'normalRange',
      width: 130,
    },
    {
      title: t('Unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 60,
    },
    {
      title: t('Updated At'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 140,
      render: (text: string) => formatDateColumn(text),
    },
  ];

  return (
    <Modal
      destroyOnClose
      className="vindr-modal add-order max-w-screen w-[900px]"
      footer={null}
      maskClosable={false}
      open={visible}
      style={{ top: 10 }}
      title={t('Clinical Information')}
      zIndex={1001}
      onCancel={onClose}
    >
      <div className="relative flex w-full items-center justify-between rounded-t-sm bg-background-dark-7">
        <Dropdown
          matchTriggerwidth
          overlayClassName={cx('max-h-screen overflow-auto')}
          placement="bottomLeft"
          trigger={['click']}
          menu={{
            items: institutionMenuItems,
            selectable: true,
            selectedKeys: [selectedInstitution?.id],
          }}
        >
          <div className="flex w-full py-1 px-2 pr-2">
            <CaretDownOutlined className="mr-2 cursor-pointer" />
            <span className="inline-flex w-full flex-1 gap-[4px] overflow-hidden whitespace-nowrap">
              {selectedInstitution?.name}
            </span>
          </div>
        </Dropdown>
      </div>
      {resultByDate?.length ? (
        <Collapse
          accordion
          className="test-result-collapse [&_.ant-collapse-header]:py-2"
          defaultActiveKey={resultByDate[0]?.date}
        >
          {resultByDate?.map((section) => (
            <Panel
              key={section.date}
              forceRender
              className="p-0"
              header={formatDateColumn(dayjs(section.date, 'DD/MM/YYYY'), false)}
            >
              <Table
                className="h-[500px]"
                columns={definedColumns}
                dataSource={section.data}
                loading={isFetching}
                pagination={false}
                rowKey="id"
                scroll={{ y: 450 }}
                style={{ background: '#2c5166' }}
                wrapperClassName="h-auto"
              />
            </Panel>
          ))}
        </Collapse>
      ) : (
        <Empty />
      )}
    </Modal>
  );
};

const transformTestResult = (categories: TestInformation['categories']) => {
  const result = [];
  for (const category of categories) {
    result.push({
      level: 1,
      name: category.category_name,
      result: '',
      normalRange: '',
      unit: '',
      updateTime: '',
    });
    for (const profile of category.profiles) {
      result.push({
        level: 2,
        name: profile.profile_name,
        result: '',
        normalRange: '',
        unit: '',
        updateTime: '',
      });
      for (const data of profile.data) {
        result.push({
          level: 3,
          name: data.test_name,
          result: data.result,
          normalRange: data.normal_range,
          lowerLimit: data.lower_limit,
          higherLimit: data.higher_limit,
          unit: data.unit,
          updateTime: data.updated_at,
        });
      }
    }
  }
  return result;
};

export default TestResult;
